import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FlaskConicalIcon, LayersIcon, LayoutGridIcon, TreePineIcon, UsersIcon, TimerIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const BRAND = 'YMPÄRISTÖOPPI VALMIS™';
export const heroGuarantees = ['Välitön digitaalinen lataus', 'Kertamaksu', 'Ei tilausta', '7 päivän tyytyväisyystakuu'];
export const stats = [{
  value: '240',
  label: 'Sivua pääosissa'
}, {
  value: '6',
  label: 'Digitaalista osaa'
}, {
  value: '6',
  label: 'Luokka-astetta – 1. luokalta 6. luokalle'
}, {
  value: '5',
  label: 'Bonusta mukana'
}, {
  value: '3',
  label: 'Tasoa jokaisessa monisteessa'
}];
export const differenceStats = [{
  value: '1.–6. luokka',
  label: 'Koko alakoulun ympäristöoppi'
}, {
  value: '6 digitaalista osaa',
  label: 'Tutkimuksista ja kokeista luontoon, fysiikkaan ja kemiaan, maantietoon, terveyteen ja arviointiin'
}, {
  value: '30 turvallista koetta',
  label: 'Arjen välineillä – ei laboratoriota, ei kalliita tarvikkeita'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Illat ja viikonloput katoavat',
  text: 'Suunnittelet ensi viikon ympäristöopin tunteja ruudun ääressä – ja silti tunti alkaa usein improvisoinnilla.'
}, {
  title: 'Jokainen tunti suunnitellaan tyhjästä',
  text: 'Tavoite, aloitus, tutkimus, välineet ja moniste – joka ikiselle tunnille erikseen.'
}, {
  title: '25 oppilasta, hyvin eri tasoilla',
  text: 'Osa kysyy jo atomeista, osa tarvitsee tukea lukemiseen – ja saman tunnin pitäisi toimia kaikille.'
}, {
  title: 'Kokeet jäävät tekemättä',
  text: 'Tutkiva oppiminen kuulostaa hyvältä, mutta välineiden keksiminen, turvallisuus ja aika jäävät aina opettajan harteille.'
}, {
  title: 'Arviointi on hankalaa perustella',
  text: 'Arviointikriteerit, havainnointi, itsearviointi ja numerot 4–10 – miten arvioit ympäristöoppia reilusti ja dokumentoit sen?'
}, {
  title: 'Viisi tiedonalaa yhdessä oppiaineessa',
  text: 'Biologia, maantieto, fysiikka, kemia ja terveystieto – kaikki pitäisi hallita ja suunnitella yhtä hyvin.'
}];
export const afterItems = ['Valmis vuosisuunnitelma 1.–2. ja 3.–6. luokille', '12 valmista ympäristöopin tuntia minuutti minuutilta', 'Tavoite, välineet ja opettajan ohje jokaiselle tunnille', '30 turvallista koetta arjen välineillä', 'Tulostettavat tehtävämonisteet', 'Jokainen moniste kolmella tasolla', 'Rakennettu POPS 2014:n ympäristöopin tavoitteiden pohjalta', 'Luonto, fysiikka, kemia, maantieto ja terveys', 'Kokeet A/B vastauksineen, arviointimatriisit ja 100 valmista palautekommenttia'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Illat kuluvat suunnitteluun',
  text: 'Käytätkö iltoja ja viikonloppuja ensi viikon ympäristöopin tuntien suunnitteluun?'
}, {
  icon: TimerIcon,
  title: 'Tunti alkaa ennen kuin olet valmis',
  text: 'Kävelet luokkaan ja mietit vielä, miten aloitat ja mitä välineitä tarvitaan?'
}, {
  icon: UsersIcon,
  title: 'Ryhmässä on suuret tasoerot',
  text: 'Tarvitsetko monisteita, jotka toimivat sekä tukea tarvitseville että nopeasti eteneville?'
}, {
  icon: FlaskConicalIcon,
  title: 'Kokeet jäävät tekemättä',
  text: 'Haluaisitko tehdä enemmän kokeita, mutta ideat, välineet ja turvallisuus vievät liikaa aikaa?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Arviointi tuntuu epämääräiseltä',
  text: 'Haluaisitko valmiit kokeet, kriteerit ja palautekommentit, joilla arviointi on reilua ja perusteltua?'
}, {
  icon: TreePineIcon,
  title: 'Ulos oppimaan – mutta miten?',
  text: 'Kaipaatko valmiita metsäretkiä ja ulko-opetustuokioita, jotka toimivat jokaisena vuodenaikana?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Osa 01',
  level: '1.–6. luokka',
  title: 'Vuosisuunnitelma ja 12 valmista tuntia',
  tagline: 'Tiedät tarkalleen, mitä jokaisella tunnilla tehdään – koko lukuvuoden.',
  intro: 'Ensimmäinen osa antaa sinulle valmiit vuosisuunnitelmat kaikille vuosiluokille sekä kaksitoista ympäristöopin tuntia, jotka voit pitää heti.',
  items: ['Vuosisuunnitelmat 1.–2. ja 3.–6. luokille', '38 kouluviikkoa POPS-tavoitteisiin linkitettynä', '12 valmista tuntia minuutti minuutilta', '45 minuutin tunnit', 'Tavoite, aloitus, tutkimus ja koonti', 'Välineet ja opettajan ohje jokaiselle tunnille', 'Turvallisuus tutkimustunnilla', 'Tulostettavat monisteet kolmella tasolla', 'Pikakokeet, vastaukset ja arviointimatriisi'],
  outro: 'Valmiit tunnit, joiden ansiosta menet luokkaan selkeän suunnitelman kanssa.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Osa 02',
  level: '1.–6. luokka',
  title: 'Tutkimukset ja kokeet',
  tagline: '30 turvallista koetta arjen välineillä.',
  intro: 'Toinen osa tekee tutkivasta oppimisesta helppoa: kysymys, hypoteesi, koe ja päätelmä – välineillä, jotka löytyvät koulusta tai kotoa.',
  items: ['Tutkimuksen vaiheet', 'Hypoteesi – koe – päätelmä', 'Tutkimusraporttipohjat', 'Turvallisuussäännöt', '30 koetta: vesi, ilma, kasvit, magneetit, valo, ääni, sähkö', 'Välinelistat ja opettajan ohjeet', 'Monisteet kolmella tasolla'],
  outro: 'Kokeita, jotka onnistuvat ensimmäisellä kerralla – ilman laboratoriota.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 03',
  level: '1.–6. luokka',
  title: 'Luonto ja eliöt',
  tagline: 'Suomen luonto lähelle – metsästä ravintoverkkoihin.',
  intro: 'Kolmas osa vie oppilaat kasvien, eläinten ja ekosysteemien maailmaan suomalaisten vuodenaikojen tahdissa.',
  items: ['Kasvit ja yhteyttäminen', 'Eläimet ja elinympäristöt', 'Metsä ekosysteeminä', 'Ravintoketjut ja -verkot', 'Vuodenajat Suomessa', 'Lajintunnistus ja lajikortit', 'Eliöiden elinkaaret', 'Luonnon monimuotoisuus ja jokamiehenoikeudet'],
  outro: 'Luontotunnit, joissa oppilaat havainnoivat, tunnistavat ja ymmärtävät.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Osa 04',
  level: '1.–6. luokka',
  title: 'Fysiikka ja kemia arjessa',
  tagline: 'Ilmiöt, jotka oppilaat näkevät joka päivä.',
  intro: 'Neljäs osa selittää fysiikan ja kemian ilmiöt arjen esimerkeillä ja pienillä kokeilla.',
  items: ['Aineet ja niiden ominaisuudet', 'Olomuodot ja veden kiertokulku', 'Seokset ja liukeneminen', 'Ilma', 'Voima ja liike', 'Sähkö ja virtapiirit', 'Magnetismi', 'Valo, varjo ja ääni', 'Energia ja sen säästäminen'],
  outro: 'Selkeät tunnit, joissa vaikeatkin ilmiöt avautuvat jokaiselle.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 05',
  level: '1.–6. luokka',
  title: 'Maantieto, ihminen ja terveys',
  tagline: 'Kartasta kehoon – ja kestävään tulevaisuuteen.',
  intro: 'Viides osa yhdistää maantiedon, ihmisen kehon ja terveystiedon valmiiksi tunneiksi.',
  items: ['Kartta ja karttamerkit', 'Suomen maakunnat ja luonto', 'Maailman maanosat', 'Sää ja ilmasto', 'Aurinkokunta ja vuorokausi', 'Ihmisen keho ja elimistö', 'Kasvu ja murrosikä', 'Terveelliset elämäntavat', 'Turvallisuus ja liikenne', 'Kestävä tulevaisuus'],
  outro: 'Valmiit tunnit, joiden avulla oppilas ymmärtää maailmaa ja itseään.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 06',
  level: '1.–6. luokka',
  title: 'Arviointi',
  tagline: 'Reilu, perusteltu ja selkeä ympäristöopin arviointi.',
  intro: 'Kuudes osa kokoaa arvioinnin työkalut: kriteerit, kokeet, havainnoinnin, itse- ja vertaisarvioinnin sekä valmiit palautteet.',
  items: ['Arviointikriteerit 6. luokan päättyessä (arvosana 8) selkokielellä', 'Kokeet A/B jokaisesta aihekokonaisuudesta vastauksineen', 'Havainnointilomakkeet tutkimustunnille', 'Itse- ja vertaisarviointi', 'Arviointimatriisi kolmella tasolla', '100 valmista palautekommenttia'],
  outro: 'Kattava tuki oikeudenmukaiseen arviointiin – ilman iltoja paperien parissa.',
  note: 'Materiaali on itsenäisesti laadittu, eikä se ole Opetushallituksen virallinen julkaisu.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitaalista osaa ympäristöoppiin',
  text: 'Kokonainen opetusjärjestelmä, joka auttaa suunnittelemaan ja toteuttamaan ympäristöopin opetuksen koko lukuvuoden ajan. Jokaisessa osassa on 40 sivua valmiita tunteja, kokeita, monisteita kolmella tasolla ja vastauksia.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Valmiit tunnit minuutti minuutilta',
  text: 'Ei enää tyhjää paperia. Tavoite, aloitus, tutkimus, koonti, välineet ja opettajan ohje – jokainen 45 minuutin tunti valmiina.'
}, {
  icon: FlaskConicalIcon,
  title: '30 turvallista koetta',
  text: 'Arjen välineillä tehtävät kokeet välinelistoineen, turvallisuusohjeineen ja tutkimusraporttipohjineen.'
}, {
  icon: FileTextIcon,
  title: 'Kolme tasoa',
  text: 'Sama moniste kolmella tasolla – tuki, perus ja haaste – jotta jokainen oppilas pääsee onnistumaan.'
}, {
  icon: LayersIcon,
  title: 'POPS 2014:n pohjalta',
  text: 'Ympäristöopin tavoitteet ja sisällöt vuosiluokille 1–2 ja 3–6: tutkiva oppiminen, luonto, ilmiöt, ihminen ja kestävä tulevaisuus.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Arviointi valmiina',
  text: 'Kokeet A/B vastauksineen, arviointimatriisit, kriteerit 6. luokalle sekä 100 valmista palautekommenttia.'
}, {
  icon: CalendarCheckIcon,
  title: 'Vuosisuunnitelma',
  text: '38 kouluviikkoa jäsenneltynä – metsäretket, kokeet ja vuodenajat oikeissa kohdissa lukuvuotta.'
}];
// Real pages from Osa 1 (Gamma Estudio; WebP in Impultienda Recursos, store b0b666ef-...).
const PAGE_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/b0b666ef-f5fc-4598-ad01-96610d9f29f0/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: `${PAGE_BASE}1792786681972_ymp-osa1-sivu01-65e49d2a.webp`,
  label: 'Osa 1 · Kansilehti',
  title: 'Vuosisuunnitelma + 12 tuntia minuutti minuutilta',
  text: 'Osa 1 kattaa vuosiluokat 1.–6. POPS 2014:n ympäristöopin tavoitteiden pohjalta.'
}, {
  src: `${PAGE_BASE}1792786688657_ymp-osa1-sivu05-5b4f6d01.webp`,
  label: 'Osa 1 · Sivu 5',
  title: 'Vuosisuunnitelma · 1.–2. luokka · 38 kouluviikkoa',
  text: 'Jaksot, viikot, aiheet ja tavoitteet valmiina – syksystä kevääseen.'
}, {
  src: `${PAGE_BASE}1792786693096_ymp-osa1-sivu14-1b005243.webp`,
  label: 'Osa 1 · Sivu 14',
  title: 'Tunti 2 · Ruska – miksi lehdet vaihtavat väriä?',
  text: 'Valmis tunti: tavoite, välineet, kulku minuutti minuutilta ja opettajan ohje.'
}, {
  src: `${PAGE_BASE}1792786697650_ymp-osa1-sivu27-899a227a.webp`,
  label: 'Osa 1 · Sivu 27',
  title: 'Tunti 7 · Metsän ravintoketju',
  text: 'Suomen metsän eliöt ja ravintoketjut – tutkien ja havainnoiden.'
}, {
  src: `${PAGE_BASE}1792786702478_ymp-osa1-sivu39-6427dbec.webp`,
  label: 'Osa 1 · Sivu 39',
  title: 'Arviointimatriisi',
  text: 'Tutkiminen, tiedot, käsitteet ja yhteistyö neljällä tasolla – arviointi selkeästi.'
}];
export const pageTypes = ['Valmiit tunnit', 'Kokeet', 'Monisteet', 'Kokeet ja vastaukset', 'Arviointi'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Ympäristöopin aloitus 5 minuutissa',
  text: '40 valmista tunnin avausta: arvoituksia, «mitä tapahtuu jos…?» -kysymyksiä, kuvakysymyksiä ja minidemoja.',
  items: ['40 tunnin avausta', 'Vastaukset mukana', '5 minuuttia kerrallaan', 'Sopii 1.–6. luokille'],
  benefit: 'Jokainen tunti alkaa uteliaisuudella.'
}, {
  title: 'Ulos oppimaan – 30 valmista ulko-opetustuokiota',
  text: 'Metsäretket, hyönteisjahti, sääasema, varjon mittaus ja lajikortit – jokaiseen vuodenaikaan.',
  items: ['30 ulko-opetustuokiota', 'Syksy, talvi ja kevät', 'Välineet ja turvallisuus', 'Tulostettavat tehtäväkortit'],
  benefit: 'Luonto oppimisympäristöksi ilman lisäsuunnittelua.'
}, {
  title: 'Koti–koulu-viestintä – valmiit Wilma-viestit ja kirjeet huoltajille',
  text: 'Valmiit viestipohjat huoltajille: retket, kokeet, varusteet, luokkaretki ja arviointikeskustelu.',
  items: ['Valmiit Wilma-viestit', 'Kirjeet huoltajille', 'Retket ja varusteet', 'Arviointikeskustelun pohja'],
  benefit: 'Selkeä viestintä kotiin – muutamassa sekunnissa.'
}, {
  title: 'Sijaiskansio ympäristöoppiin – 15 valmista tuntia',
  text: 'Viisitoista tuntia, jotka sijainen voi pitää ilman valmistelua: ohje, moniste ja vastaukset.',
  items: ['15 valmista 45 minuutin tuntia', '1.–2. ja 3.–6. luokille', 'Ohje sijaiselle', 'Monisteet vastauksineen'],
  benefit: 'Ei hukattuja tunteja, kun olet poissa.'
}, {
  title: 'Julisteet luokkaan – 20 tulostettavaa julistetta',
  text: 'Tutkimuksen vaiheet, turvallisuussäännöt, veden kiertokulku, ravintoketju, aurinkokunta, ihmisen elimet, Suomen kartta ja sanasto.',
  items: ['20 julistetta', 'Tulostusvalmis A4 ja A3', 'Selkeät kuvat ja vähän tekstiä', 'Sanasto'],
  benefit: 'Luokan seinät opettavat puolestasi.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Avaa YMPÄRISTÖOPPI VALMIS™',
  text: 'Lataa paketti ja avaa luokka-asteellesi sopiva osa.'
}, {
  title: 'Valitse luokka ja tunti',
  text: 'Löydä ryhmällesi sopiva tunti, koe tai moniste.'
}, {
  title: 'Valmistaudu 5 minuutissa',
  text: 'Lue opettajan ohje, katso välinelista ja tulosta moniste.'
}, {
  title: 'Mene luokkaan',
  text: 'Pidä materiaali tabletilla tai tulosteena – ja aloita tunti selkeän suunnitelman kanssa.'
}];
export const audienceItems = ['Opetat ympäristöoppia 1.–6. luokalla', 'Olet luokanopettaja ja haluat säästää suunnitteluaikaa', 'Olet erityisopettaja ja tarvitset porrastettuja monisteita', 'Haluat valmiin vuosisuunnitelman POPS 2014:n pohjalta', 'Haluat tehdä enemmän kokeita ja tutkimuksia', 'Opetat ryhmiä, joissa on suuret tasoerot', 'Haluat tukea ympäristöopin arviointiin', 'Haluat säästää iltasi ja viikonloppusi'];
export const timeValueTasks = ['Tehtävien ja kokeiden etsiminen netistä', 'Tuntien ja monisteiden suunnittelu', 'Monisteiden porrastaminen eri tasoille', 'Kokeiden välineet ja turvallisuus', 'Arviointi ja palautteen antaminen'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitaalista osaa – 240 sivua',
  detail: 'Vuosisuunnitelma ja valmiit tunnit, tutkimukset ja kokeet, luonto ja eliöt, fysiikka ja kemia, maantieto, ihminen ja terveys sekä arviointi'
}, {
  label: '5 bonusta ilman lisämaksua',
  detail: 'Aloitus 5 minuutissa, Ulos oppimaan, Koti–koulu-viestintä, Sijaiskansio ja Julisteet luokkaan'
}, {
  label: '1.–6. luokille',
  detail: 'Valmiit tunnit, kokeet, monisteet kolmella tasolla, vastaukset ja arviointi'
}];
export const offerIncludes = ['Välitön digitaalinen lataus', 'Tallennettavat PDF-tiedostot', 'Tulostusvalmis', 'Fyysistä tuotetta ei lähetetä'];
export const PRICE = '19 €';
export const PRICE_BEFORE = '55 €';

// Only add authentic reviews of YMPÄRISTÖOPPI VALMIS. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Miten saan materiaalin?',
  a: 'YMPÄRISTÖOPPI VALMIS™ toimitetaan digitaalisesti. Oston jälkeen saat käyttöösi PDF-tiedostot, jotka voit ladata ja tallentaa. Voit käyttää materiaalia tietokoneella, tabletilla tai puhelimella ja tulostaa tarvitsemasi sivut.'
}, {
  q: 'Onko materiaali POPS 2014:n mukainen?',
  a: 'Materiaali on rakennettu perusopetuksen opetussuunnitelman perusteiden (POPS 2014) ympäristöopin tavoitteiden ja sisältöjen pohjalta vuosiluokille 1–2 ja 3–6. YMPÄRISTÖOPPI VALMIS™ on itsenäisesti laadittu opetusmateriaali, eikä se ole Opetushallituksen virallinen julkaisu.'
}, {
  q: 'Mille luokille materiaali sopii?',
  a: 'Materiaali kattaa luokat 1–6. Jokaisessa osassa on sisältöä sekä 1.–2. että 3.–6. luokille, ja monisteet on porrastettu kolmelle tasolle. Valitset tunnit oman ryhmäsi mukaan.'
}, {
  q: 'Voinko todella valmistella tunnin 5 minuutissa?',
  a: 'YMPÄRISTÖOPPI VALMIS™ on tehty nopeuttamaan valmistelua. Avaat osan, valitset valmiin tunnin, luet opettajan ohjeen ja tulostat monisteen noin viidessä minuutissa. Kokeissa aika voi vaihdella välineiden mukaan.'
}, {
  q: 'Tarvitaanko kokeisiin erityisiä välineitä?',
  a: 'Ei. Osan 2 kokeet on suunniteltu arjen välineille, jotka löytyvät koulusta tai kotoa: vettä, suolaa, magneetteja, taskulamppuja, paristoja ja kasveja. Jokaisessa kokeessa on välinelista ja turvallisuusohje.'
}, {
  q: 'Riittääkö materiaali koko lukuvuodeksi?',
  a: 'YMPÄRISTÖOPPI VALMIS™ sisältää vuosisuunnitelmat 38 kouluviikolle sekä valmiit tunnit ja kokonaisuudet kaikissa osissa. Valitset, yhdistelet ja muokkaat materiaalia oman suunnitelmasi ja oppilaidesi mukaan.'
}, {
  q: 'Toimiiko materiaali, jos ryhmässä on suuret tasoerot?',
  a: 'Kyllä. Monisteet on porrastettu kolmelle tasolle – tuki, perus ja haaste – joten sama tunti toimii kaikille oppilaille.'
}, {
  q: 'Sisältyykö arviointi?',
  a: 'Kyllä. Osa 6 sisältää arviointikriteerit 6. luokan päättyessä (arvosana 8), kokeet A/B vastauksineen, havainnointilomakkeet, itse- ja vertaisarvioinnin, arviointimatriisin ja 100 valmista palautekommenttia.'
}, {
  q: 'Pitääkö kaikki tulostaa?',
  a: 'Ei. Valitset itse, mitkä sivut tulostat. Materiaali toimii myös tabletilta tai luokan näytöltä.'
}, {
  q: 'Sisältyvätkö viisi bonusta hintaan?',
  a: `Kyllä. Kaikki viisi bonusta sisältyvät pakettiin ilman lisämaksua – kaikki yhteensä ${PRICE}.`
}, {
  q: 'Onko kyseessä tilaus?',
  a: 'Ei. Maksat kerran ja saat digitaalisen materiaalin käyttöösi. Ei toistuvia veloituksia.'
}, {
  q: 'Voinko käyttää materiaalia puhelimella tai tabletilla?',
  a: 'Kyllä. PDF-tiedostot aukeavat tietokoneella, tabletilla ja puhelimella.'
}, {
  q: 'Onko tuotteella takuu?',
  a: 'Kyllä. YMPÄRISTÖOPPI VALMIS™ -paketilla on 7 päivän tyytyväisyystakuu. Jos materiaali ei vastaa odotuksiasi, voit ottaa meihin yhteyttä ja pyytää rahat takaisin takuuajan sisällä ehtojen mukaisesti.'
}];
export const footerLinks = ['Ostoehdot', 'Tietosuoja', 'Palautukset', 'Yhteystiedot'];
